import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "expanded", "onChange", "onPanelFocus"];
import PropTypes from 'prop-types';
import React, { Children, forwardRef, useCallback, useMemo, useState } from 'react';
import { KEY_CODES } from '../../constants';
import { identifier } from '../../util/identifier';
import { AccordionItemContext } from '../AccordionItem/AccordionItem.context';
import { StyledAccordion } from './Accordion.styles';
import { getNextState } from './getNextState';

/**
 * See `Accordion` for documentation.
 */

export var ControlledAccordion = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var children = _ref.children,
    expanded = _ref.expanded,
    onChange = _ref.onChange,
    onPanelFocus = _ref.onPanelFocus,
    rest = _objectWithoutProperties(_ref, _excluded);
  var childrenCount = Children.count(children);

  // Manage panel header focus
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    focusedPanelIndex = _useState2[0],
    setFocusedPanelIndex = _useState2[1];

  // Ref callback to focus on selected panel header
  var focusRef = useCallback(function (element) {
    if (element) {
      element.focus();
      if (typeof onPanelFocus === 'function') {
        onPanelFocus(focusedPanelIndex);
      }
    }
  }, [focusedPanelIndex, onPanelFocus]);

  // Focus on the first panel header
  var onFocusFirst = useCallback(function () {
    setFocusedPanelIndex(0);
  }, [setFocusedPanelIndex]);

  // Focus on the last panel header
  var onFocusLast = useCallback(function () {
    setFocusedPanelIndex(childrenCount - 1);
  }, [childrenCount, setFocusedPanelIndex]);

  // Focus on the next or first panel header
  var onFocusNext = useCallback(function () {
    if (focusedPanelIndex !== null && focusedPanelIndex >= childrenCount - 1) {
      onFocusFirst();
    } else {
      setFocusedPanelIndex(focusedPanelIndex + 1);
    }
  }, [childrenCount, focusedPanelIndex, onFocusFirst, setFocusedPanelIndex]);

  // Focus on the previous or last panel header
  var onFocusPrevious = useCallback(function () {
    if (focusedPanelIndex !== null && focusedPanelIndex <= 0) {
      onFocusLast();
    } else {
      setFocusedPanelIndex(focusedPanelIndex - 1);
    }
  }, [focusedPanelIndex, onFocusLast, setFocusedPanelIndex]);

  // Reset panel header focus on blur
  var onBlur = useCallback(function () {
    setFocusedPanelIndex(null);
  }, [setFocusedPanelIndex]);

  // Call `onChange` prop with selected panel index and next state
  var _onClick = useCallback(function (panelIndex) {
    onChange(panelIndex, getNextState(panelIndex, expanded));
  }, [expanded, onChange]);

  // Focus on selected panel header
  var _onFocus = useCallback(function (panelIndex) {
    setFocusedPanelIndex(panelIndex);
  }, [setFocusedPanelIndex]);

  // Manage keyboard navigation
  var _onKeyDown = useCallback(function (panelIndex, event) {
    if (event.keyCode === KEY_CODES.SPACE) {
      _onClick(panelIndex);
    } else if (event.keyCode === KEY_CODES.ENTER) {
      _onClick(panelIndex);
    } else if (event.keyCode === KEY_CODES.ARROW_DOWN) {
      onFocusNext();
    } else if (event.keyCode === KEY_CODES.ARROW_UP) {
      onFocusPrevious();
    } else if (event.keyCode === KEY_CODES.HOME) {
      onFocusFirst();
    } else if (event.keyCode === KEY_CODES.END) {
      onFocusLast();
    } else {
      return false;
    }
    event.preventDefault();
    return true;
  }, [_onClick, onFocusNext, onFocusPrevious, onFocusFirst, onFocusLast]);

  // Provider context for each accordion item
  var itemContext = useCallback(function (panelIndex) {
    return {
      expanded: Array.isArray(expanded) ? expanded.indexOf(panelIndex) !== -1 : expanded === panelIndex,
      headerId: identifier(),
      panelId: identifier(),
      ref: focusedPanelIndex === panelIndex ? focusRef : undefined,
      onBlur: onBlur,
      onClick: function onClick() {
        return _onClick(panelIndex);
      },
      onFocus: function onFocus() {
        return _onFocus(panelIndex);
      },
      onKeyDown: function onKeyDown(event) {
        return _onKeyDown(panelIndex, event);
      }
    };
  }, [expanded, focusRef, focusedPanelIndex, onBlur, _onClick, _onFocus, _onKeyDown]);

  // Generate accordion items with context based on child index
  var items = useMemo(function () {
    return Children.map(children, function (child, index) {
      return child && /*#__PURE__*/React.createElement(AccordionItemContext.Provider, {
        value: itemContext(index)
      }, child);
    });
  }, [children, itemContext]);

  // Render
  return /*#__PURE__*/React.createElement(StyledAccordion, _extends({
    ref: forwardedRef
  }, rest), items);
});
ControlledAccordion.displayName = 'ControlledAccordion';

// Props are documented in `Accordion.jsx`
ControlledAccordion.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.node.isRequired,
  expanded: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
  onChange: PropTypes.func,
  onPanelFocus: PropTypes.func
} : {};